import XLSX from 'xlsx-js-style';

import { parseDrawingObjects, parseLinksAndNotes } from './annotations';
import { parseConditionalFormats, parseSheetFeatures } from './features';
import {
  loadAndPreflightOoxmlPackage,
  type OoxmlPackageReader,
} from './packageReader';
import {
  buildParsedStyles,
  parseBordersFromStylesXml,
  parseCellXfStyleSupplements,
  type ParsedCellXfStyleSupplements,
  type RawBorder,
} from './styles';
import {
  XlsxParseLimitError,
  type XlsxCellKey,
  type XlsxParseLimits,
  type XlsxParseOptions,
  type XlsxParsedCell,
  type XlsxParsedColumnDimension,
  type XlsxParsedMerge,
  type XlsxParsedRowDimension,
  type XlsxParsedScalar,
  type XlsxParsedSheet,
  type XlsxParseSession,
} from './types';
import { parseXmlAttributes } from './xml';

export const DEFAULT_XLSX_PARSE_LIMITS: Readonly<XlsxParseLimits> = Object.freeze({
  maxArchiveEntries: 4_096,
  maxUncompressedBytes: 256 * 1024 * 1024,
  maxSheets: 64,
  maxRows: 200_000,
  maxColumns: 4_096,
  maxMaterializedCells: 250_000,
  maxXmlEntryBytes: 64 * 1024 * 1024,
  maxSharedStringsBytes: 32 * 1024 * 1024,
  maxStylesBytes: 16 * 1024 * 1024,
});

function mergedLimits(overrides: Partial<XlsxParseLimits> | undefined): XlsxParseLimits {
  const result: XlsxParseLimits = { ...DEFAULT_XLSX_PARSE_LIMITS, ...overrides };
  for (const [name, value] of Object.entries(result)) {
    if (!Number.isFinite(value) || value <= 0) {
      throw new TypeError(`XLSX parse limit ${name} must be a positive finite number.`);
    }
    (result as unknown as Record<string, number>)[name] = Math.trunc(value);
  }
  return result;
}

function abortError(): Error {
  const error = new Error('XLSX parsing was aborted.');
  error.name = 'AbortError';
  return error;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw abortError();
}

function normalizedSource(source: ArrayBuffer | Uint8Array): Uint8Array {
  if (source instanceof Uint8Array) return new Uint8Array(source);
  return new Uint8Array(source.slice(0));
}

/**
 * The parser keeps an immutable native-workbook source for lazy sheet parsing,
 * while consumers receive an editable clone. Renderers are allowed to rename,
 * reorder, add, and delete sheets without invalidating a later cache miss.
 */
function cloneNativeWorkbook(source: XLSX.WorkBook): XLSX.WorkBook {
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(source);
  }
  // `structuredClone` is available in supported runtimes. This fallback keeps
  // older embedded WebViews functional for the plain data shapes SheetJS emits.
  const clone = (value: unknown, seen: WeakMap<object, unknown>): unknown => {
    if (value === null || typeof value !== 'object') return value;
    if (value instanceof Date) return new Date(value.getTime());
    if (value instanceof ArrayBuffer) return value.slice(0);
    if (ArrayBuffer.isView(value)) {
      if (value instanceof DataView) {
        const buffer = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
        return new DataView(buffer);
      }
      const typed = value as Uint8Array;
      return typed.slice();
    }
    const cached = seen.get(value);
    if (cached !== undefined) return cached;
    if (Array.isArray(value)) {
      const output: unknown[] = [];
      seen.set(value, output);
      for (const entry of value) output.push(clone(entry, seen));
      return output;
    }
    const output: Record<string, unknown> = {};
    seen.set(value, output);
    for (const [key, entry] of Object.entries(value)) output[key] = clone(entry, seen);
    return output;
  };
  return clone(source, new WeakMap()) as XLSX.WorkBook;
}

function parsedScalar(value: unknown): XlsxParsedScalar {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value instanceof Date) return new Date(value.getTime());
  return String(value);
}

function workbookActiveSheetIndex(
  workbook: XLSX.WorkBook,
  workbookXml: string | null,
): number {
  const metadata = workbook.Workbook as
    | { WBView?: Array<{ activeTab?: number | string }> }
    | undefined;
  let raw: unknown = metadata?.WBView?.[0]?.activeTab;
  if (raw === undefined && workbookXml) {
    const view = /<(?:\w+:)?workbookView\b([^>]*?)\/?\s*>/i.exec(workbookXml);
    if (view) raw = parseXmlAttributes(view[1] ?? '').activeTab;
  }
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 0 || workbook.SheetNames.length === 0) return 0;
  return Math.min(value, workbook.SheetNames.length - 1);
}

function validateRange(
  sourceRange: string | undefined,
  limits: XlsxParseLimits,
  sheetName: string,
): { rowCount: number; colCount: number } {
  if (!sourceRange) return { rowCount: 0, colCount: 0 };
  let range: XLSX.Range;
  try {
    range = XLSX.utils.decode_range(sourceRange.replace(/\$/g, '').toUpperCase());
  } catch {
    throw new Error(`Invalid XLSX worksheet range in ${sheetName}: ${sourceRange}`);
  }
  const rowCount = range.e.r + 1;
  const colCount = range.e.c + 1;
  if (rowCount > limits.maxRows) {
    throw new XlsxParseLimitError('rows', `XLSX worksheet ${sheetName} exceeds ${limits.maxRows} rows.`);
  }
  if (colCount > limits.maxColumns) {
    throw new XlsxParseLimitError('columns', `XLSX worksheet ${sheetName} exceeds ${limits.maxColumns} columns.`);
  }
  return { rowCount, colCount };
}

function parsedMerges(merges: XLSX.Range[]): XlsxParsedMerge[] {
  return merges.map((merge) => ({
    range: XLSX.utils.encode_range(merge),
    start: { row: merge.s.r, col: merge.s.c },
    end: { row: merge.e.r, col: merge.e.c },
  }));
}

function parsedColumnDimensions(worksheet: XLSX.WorkSheet): XlsxParsedColumnDimension[] {
  const source = worksheet['!cols'] ?? [];
  const output: XlsxParsedColumnDimension[] = [];
  source.forEach((entry, index) => {
    if (!entry) return;
    const raw = entry as XLSX.ColInfo & { width?: number; hidden?: boolean };
    const dimension: XlsxParsedColumnDimension = { index };
    if (typeof raw.width === 'number') dimension.width = raw.width;
    if (typeof raw.wpx === 'number') dimension.wpx = raw.wpx;
    if (typeof raw.wch === 'number') dimension.wch = raw.wch;
    if (typeof raw.hidden === 'boolean') dimension.hidden = raw.hidden;
    output.push(dimension);
  });
  return output;
}

function parsedRowDimensions(worksheet: XLSX.WorkSheet): XlsxParsedRowDimension[] {
  const source = worksheet['!rows'] ?? [];
  const output: XlsxParsedRowDimension[] = [];
  source.forEach((entry, index) => {
    if (!entry) return;
    const raw = entry as XLSX.RowInfo & { hidden?: boolean };
    const dimension: XlsxParsedRowDimension = { index };
    if (typeof raw.hpt === 'number') dimension.hpt = raw.hpt;
    if (typeof raw.hpx === 'number') dimension.hpx = raw.hpx;
    if (typeof raw.hidden === 'boolean') dimension.hidden = raw.hidden;
    output.push(dimension);
  });
  return output;
}

class ParseSession implements XlsxParseSession {
  readonly workbook: XLSX.WorkBook;
  readonly sheetNames: readonly string[];
  readonly activeSheetIndex: number;
  private readonly sheetCache = new Map<number, Promise<XlsxParsedSheet>>();
  private legacyMaterializedCells = 0;

  constructor(
    private readonly sourceWorkbook: XLSX.WorkBook,
    activeSheetIndex: number,
    private readonly reader: OoxmlPackageReader | null,
    private readonly sheetPaths: readonly string[],
    private readonly stylesXml: string | null,
    private readonly borders: RawBorder[],
    private readonly cellXfSupplements: ParsedCellXfStyleSupplements,
    private readonly limits: XlsxParseLimits,
    private readonly signal?: AbortSignal,
  ) {
    this.workbook = cloneNativeWorkbook(sourceWorkbook);
    this.sheetNames = Object.freeze([...sourceWorkbook.SheetNames]);
    this.activeSheetIndex = activeSheetIndex;
  }

  parseSheet(index: number): Promise<XlsxParsedSheet> {
    if (!Number.isInteger(index) || index < 0 || index >= this.sheetNames.length) {
      return Promise.reject(new RangeError(`XLSX sheet index is out of range: ${index}.`));
    }
    let pending = this.sheetCache.get(index);
    if (!pending) {
      pending = this.parseSheetUncached(index);
      this.sheetCache.set(index, pending);
    }
    return pending;
  }

  private async parseSheetUncached(index: number): Promise<XlsxParsedSheet> {
    throwIfAborted(this.signal);
    const name = this.sheetNames[index];
    const worksheet = this.sourceWorkbook.Sheets[name];
    if (!worksheet) throw new Error(`XLSX worksheet is missing: ${name}.`);
    const sourcePath = this.sheetPaths[index] ?? null;
    const sheetXml = sourcePath && this.reader ? await this.reader.readText(sourcePath) : null;
    const sourceRange = worksheet['!ref'] ?? null;
    const extent = validateRange(sourceRange ?? undefined, this.limits, name);
    const mergeRanges = worksheet['!merges'] ?? [];
    const styleCollection = buildParsedStyles(
      worksheet,
      sheetXml,
      (this.sourceWorkbook as XLSX.WorkBook & { Styles?: unknown }).Styles,
      this.borders,
      mergeRanges,
      this.cellXfSupplements,
    );

    const cells = new Map<XlsxCellKey, XlsxParsedCell>();
    let localCellCount = 0;
    for (const address of Object.keys(worksheet)) {
      if (address.startsWith('!')) continue;
      const cell = worksheet[address];
      if (!cell) continue;
      let position: XLSX.CellAddress;
      try {
        position = XLSX.utils.decode_cell(address.replace(/\$/g, '').toUpperCase());
      } catch {
        continue;
      }
      if (position.r + 1 > this.limits.maxRows) {
        throw new XlsxParseLimitError('rows', `XLSX cell exceeds ${this.limits.maxRows} rows: ${address}.`);
      }
      if (position.c + 1 > this.limits.maxColumns) {
        throw new XlsxParseLimitError('columns', `XLSX cell exceeds ${this.limits.maxColumns} columns: ${address}.`);
      }
      localCellCount += 1;
      if (!this.reader && this.legacyMaterializedCells + localCellCount > this.limits.maxMaterializedCells) {
        throw new XlsxParseLimitError(
          'materialized_cells',
          `XLSX has too many materialized cells (> ${this.limits.maxMaterializedCells}).`,
        );
      }
      const value = parsedScalar(cell.v);
      const key: XlsxCellKey = `${position.r}:${position.c}`;
      const parsed: XlsxParsedCell = {
        row: position.r,
        col: position.c,
        address: XLSX.utils.encode_cell(position),
        value,
        displayText: cell.w ?? (value == null ? '' : String(value)),
      };
      if (typeof cell.v === 'number' && Number.isFinite(cell.v)) parsed.rawNumber = cell.v;
      if (typeof cell.f === 'string' && cell.f) parsed.formula = cell.f;
      if (typeof cell.z === 'string' && cell.z && cell.z !== 'General') parsed.numberFormat = cell.z;
      const styleIndex = styleCollection.references.cells.get(key);
      if (styleIndex !== undefined) parsed.styleIndex = styleIndex;
      cells.set(key, parsed);
      if ((localCellCount & 0x3fff) === 0) throwIfAborted(this.signal);
    }
    if (!this.reader) this.legacyMaterializedCells += localCellCount;

    const [linksAndNotes, drawing] = await Promise.all([
      parseLinksAndNotes(this.reader, sourcePath, index),
      parseDrawingObjects(this.reader, sourcePath),
    ]);
    throwIfAborted(this.signal);
    // Charts colour their series from the workbook theme; skip the read otherwise.
    const themeXml = drawing.charts.length > 0
      ? (await this.reader?.readText('xl/theme/theme1.xml')) ?? undefined
      : undefined;
    return {
      index,
      name,
      sourcePath,
      sourceRange,
      rowCount: extent.rowCount,
      colCount: extent.colCount,
      cells,
      styles: styleCollection.styles,
      rowStyles: styleCollection.rowStyles,
      columnStyles: styleCollection.columnStyles,
      merges: parsedMerges(mergeRanges),
      columns: parsedColumnDimensions(worksheet),
      rows: parsedRowDimensions(worksheet),
      features: parseSheetFeatures(sheetXml),
      conditionalFormats: parseConditionalFormats(sheetXml, this.stylesXml),
      hyperlinks: linksAndNotes.hyperlinks,
      notes: linksAndNotes.notes,
      images: drawing.images,
      charts: drawing.charts,
      themeXml,
    };
  }
}

/** Compound File Binary (legacy .xls) signature — binary, not text. */
const CFB_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0];

function startsWith(bytes: Uint8Array, signature: readonly number[]): boolean {
  if (bytes.length < signature.length) return false;
  return signature.every((byte, index) => bytes[index] === byte);
}

/**
 * Pick the codepage SheetJS should use for a **text** workbook (CSV/TSV/PRN).
 *
 * SheetJS decodes a BOM-less text workbook as cp1252, so a UTF-8 CSV with
 * Korean text renders as mojibake (`# 모델` → `# ëª¨ë¸`) while the ASCII columns
 * look fine. That is the shape of every "preview is broken but the download is
 * fine" report for CSVs.
 *
 * We only override when the bytes are **valid UTF-8**, which pure ASCII always
 * is (and for ASCII 65001 and cp1252 agree, so it is a no-op). Legacy encodings
 * fail the check and keep today's behaviour — the bundled build ships no
 * codepage tables, so `codepage: 949` decodes exactly like cp1252 and there is
 * nothing better to fall back to.
 *
 * Returns `undefined` to mean "leave it to SheetJS": for a BOM (it detects
 * UTF-8/UTF-16 itself), for ZIP/CFB binaries, and for legacy text.
 */
export function detectTextCodepage(bytes: Uint8Array): number | undefined {
  if (startsWith(bytes, [0xef, 0xbb, 0xbf])) return undefined; // UTF-8 BOM
  if (startsWith(bytes, [0xff, 0xfe]) || startsWith(bytes, [0xfe, 0xff])) {
    return undefined; // UTF-16 BOM
  }
  if (startsWith(bytes, CFB_SIGNATURE)) return undefined; // legacy .xls
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return 65001;
  } catch {
    return undefined;
  }
}

/**
 * Parse a workbook once and return a lazy, cached per-sheet parser session.
 * `File`, `Blob`, DOM nodes, CSS, and React state deliberately stay outside.
 */
export async function parseXlsxWorkbook(
  source: ArrayBuffer | Uint8Array,
  options: XlsxParseOptions = {},
): Promise<XlsxParseSession> {
  const bytes = normalizedSource(source);
  const limits = mergedLimits(options.limits);
  throwIfAborted(options.signal);
  const reader = await loadAndPreflightOoxmlPackage(bytes, limits, options.signal);
  throwIfAborted(options.signal);

  // This is the sole SheetJS read for the session. OOXML supplements use the
  // already-open package reader instead of reopening the ZIP for every feature.
  //
  // `reader === null` means the source is not an OOXML ZIP — a CSV/TSV or a
  // legacy binary. Only then does the codepage matter (see detectTextCodepage).
  const codepage = reader === null ? detectTextCodepage(bytes) : undefined;
  const workbook = XLSX.read(bytes, {
    type: 'array',
    cellStyles: true,
    cellFormula: true,
    cellHTML: false,
    cellNF: true,
    ...(codepage === undefined ? {} : { codepage }),
  });
  if (workbook.SheetNames.length === 0) throw new Error('XLSX workbook contains no worksheets.');
  if (workbook.SheetNames.length > limits.maxSheets) {
    throw new XlsxParseLimitError(
      'sheets',
      `XLSX has too many worksheets (${workbook.SheetNames.length} > ${limits.maxSheets}).`,
    );
  }

  const sheetPaths = reader ? await reader.worksheetPaths() : [];
  const stylesXml = reader ? await reader.readText('xl/styles.xml') : null;
  const borders = stylesXml ? parseBordersFromStylesXml(stylesXml) : [];
  const cellXfSupplements = stylesXml ? parseCellXfStyleSupplements(stylesXml) : [];
  const workbookXml = reader ? await reader.readText('xl/workbook.xml') : null;
  const activeSheetIndex = workbookActiveSheetIndex(workbook, workbookXml);
  return new ParseSession(
    workbook,
    activeSheetIndex,
    reader,
    sheetPaths,
    stylesXml,
    borders,
    cellXfSupplements,
    limits,
    options.signal,
  );
}
